/**
 * prepAmendmentNotes — pure helpers that shape the immutable audit notes an
 * amendment round writes to a preparation request's notes log (CLAUDE.md
 * §26.7): clipText keeps a body inside the 200-character prep-note cap and
 * amendmentNoteBodies diffs two task lists by task id. Extracted verbatim
 * from PrepRequestDetail.jsx (step P1 of the PrepRequestDetail
 * decomposition, audit §6.2). No React, no API calls.
 */

import { NOTE_BODY_MAX } from '../api/prepNotesApi';

/**
 * Shorten a task's text so an audit note fits the 200-character prep-note cap
 * (§18.4 — prep_request_notes has a CHECK constraint at 200, migration 29).
 */
export function clipText(text, max) {
  const flat = (text || '').replace(/\s+/g, ' ').trim();
  if (max <= 1) return '';
  return flat.length <= max ? flat : `${flat.slice(0, max - 1)}…`;
}

/**
 * Build one system-note body per change in an amendment round (§26.7).
 *
 * Compares the stored task list against the list being saved BY TASK ID —
 * ids are stable UUIDs assigned when a task is created, so a task keeps its
 * identity (and its completion state) across edits and reordering.
 *
 * Produces: task added / task edited (old → new) / task removed / tasks
 * reordered, each naming who made the change. Bodies are budgeted and
 * clipped to fit the prep-note cap; the final clip is a hard guarantee the
 * database CHECK can never reject an audit note.
 */
export function amendmentNoteBodies(prevTasks, nextTasks, actorName) {
  const prev = Array.isArray(prevTasks) ? prevTasks : [];
  const next = Array.isArray(nextTasks) ? nextTasks : [];
  const prevById = new Map(prev.map((t) => [t.id, t]));
  const nextById = new Map(next.map((t) => [t.id, t]));
  const bodies = [];

  for (const t of next) {
    if (prevById.has(t.id)) continue;
    const prefix = `Task added by ${actorName}: `;
    bodies.push(`${prefix}“${clipText(t.text, NOTE_BODY_MAX - prefix.length - 2)}”`);
  }

  for (const t of next) {
    const before = prevById.get(t.id);
    if (!before || before.text === t.text) continue;
    const prefix = `Task edited by ${actorName}: `;
    // Room left for “old” → “new” after the prefix and the 6 quote/arrow chars.
    const room = NOTE_BODY_MAX - prefix.length - 7;
    const half = Math.floor(room / 2);
    bodies.push(`${prefix}“${clipText(before.text, half)}” → “${clipText(t.text, room - half)}”`);
  }

  for (const t of prev) {
    if (nextById.has(t.id)) continue;
    const prefix = `Task removed by ${actorName}: `;
    bodies.push(`${prefix}“${clipText(t.text, NOTE_BODY_MAX - prefix.length - 2)}”`);
  }

  // Reordered: the same surviving tasks in a different sequence, or at a
  // different level (a sub-task promoted / a task demoted changes `parent`).
  const sig = (t) => `${t.id}:${t.parent || ''}`;
  const before = prev.filter((t) => nextById.has(t.id)).map(sig).join('|');
  const after = next.filter((t) => prevById.has(t.id)).map(sig).join('|');
  if (before !== after) bodies.push(`Tasks reordered by ${actorName}.`);

  return bodies.map((b) => clipText(b, NOTE_BODY_MAX));
}
